import {
  BannerKind,
  CardBadge,
  CardChange,
  CardState,
  Facet,
  ModelAction,
  ModelCard,
  ModelState,
  RunRow,
  runRef,
  RunStatus,
  versionRef,
  ModelsResponse,
} from '../../../shared/services/api/models/band-studio.model';

/** PrimeNG tag severities the Bands Studio pages use. */
export type TagSeverity = 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast';

/**
 * The page's name for a story section. The API titles the outputs section "What it
 * writes", which reads as a list of artefacts; it holds the bands plus post-process
 * steps the user can skip or change, so the page calls it Outputs.
 */
export function sectionTitle(sec: Pick<Facet, 'key' | 'title'>): string {
  return sec.key === 'outputs' ? 'Outputs' : sec.title;
}

/**
 * What a covers column picker says when the API lists no column: the customer's data is not
 * on this environment, or its columns could not be read (GF-6085).
 */
export const NO_FILTER_COLUMNS_MESSAGE =
  "No columns to pick from: this customer's data is not on this environment, or its columns are not known yet.";

export const RUN_STATUS_LABEL: Record<RunStatus, string> = {
  queued: 'Queued',
  running: 'Running',
  succeeded: 'Completed',
  failed: 'Failed',
};

export const RUN_STATUS_SEVERITY: Record<RunStatus, TagSeverity> = {
  queued: 'secondary',
  running: 'info',
  succeeded: 'success',
  failed: 'danger',
};

/** A section's draft changes, with the API's leading note (if any) split off from the list. */
export interface ChangeLines {
  note: string | null;
  changes: string[];
}

/**
 * The API may lead `changed_lines` with a note that the draft was not made from
 * a saved version. That line says where the draft came from, not what changed,
 * so it reads as plain text above the list instead of as one of its items.
 */
export function splitChangeLines(lines: readonly string[] | null | undefined): ChangeLines {
  const all = (lines ?? []).filter((l) => l.trim());
  const [first, ...rest] = all;
  if (first !== undefined && /saved version/i.test(first) && !first.includes('→')) {
    return { note: first, changes: rest };
  }
  return { note: null, changes: all };
}

interface RefOption {
  label: string;
  value: string;
  /** What the picker filter matches: title, short name, run name and id, so a model is found by any name. */
  search: string;
}

export interface RefGroup {
  label: string;
  items: RefOption[];
}

export interface RefPickerOptions {
  /** Only runs a draft can start from: finished ones, see {@link canStartDraftFrom}. */
  clonableOnly?: boolean;
}

/**
 * Whether a draft can start from this run. Any finished run qualifies, failed or not,
 * because a failed run's logic is often the thing to fix, and the API saves the run's
 * recipe as a version first, so outputs and a version do not matter. Only a run still
 * going, or one the API marks `clonable: false`, is left out.
 */
export function canStartDraftFrom(run: RunRow): boolean {
  // a saved version's logic is settled while its run is still going
  const settled = run.version !== null || run.status === 'succeeded' || run.status === 'failed';
  return settled && run.clonable !== false;
}

/** What a run is as a model: queued and running wait, failed is edited in place, succeeded is frozen. */
export function runState(run: Pick<RunRow, 'status'>): ModelState {
  if (run.status === 'failed') return 'failed';
  return run.status === 'succeeded' ? 'complete' : 'running';
}

/** Runs newest first by start time; a run that has not started sorts last. Returns a copy. */
export function runsNewestFirst(runs: readonly RunRow[]): RunRow[] {
  return [...runs].sort((a, b) => (b.started_at ?? '').localeCompare(a.started_at ?? ''));
}

/**
 * The buttons a model offers. The API decides them from the model's state (api#1026); a build
 * that sends none gets the same answer from the status: a failed model is edited in place, a
 * complete one only clones, a running one waits.
 */
export function modelActions(run: RunRow): ModelAction[] {
  if (run.actions) return run.actions;
  if (run.status === 'failed') return ['edit', 'clone'];
  if (run.status === 'succeeded') return canStartDraftFrom(run) ? ['clone'] : [];
  return [];
}

/** When a run started, as a picker reads it: "Oct 20, 14:02". */
export const RUN_STAMP = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/**
 * Every model of every group, for the New model and Compare pickers: "<model name> · Oct 20,
 * 14:02". A model is known by its name (10/21); the logic behind it is never a version number
 * on screen. A group's open drafts are listed first, so a draft can be compared before it
 * runs; a new model cannot start from a draft, so the clonable list leaves them out.
 */
export function refGroups(models: readonly ModelCard[], opts: RefPickerOptions = {}): RefGroup[] {
  return (
    [...models]
      // the models in no group read last, as on the Models page
      .sort(
        (a, b) =>
          Number(a.group === null) - Number(b.group === null) ||
          cardLabel(a).localeCompare(cardLabel(b))
      )
      .map((m) => {
        const group = cardLabel(m);
        const listed = m.drafts ?? (m.draft ? [m.draft] : []);
        // an older API names only that a draft is open
        const open =
          listed.length || !m.has_draft
            ? listed
            : [{ slug: m.slug, model_name: null, display_name: null }];
        const drafts = opts.clonableOnly ? [] : open;
        const draftItems = drafts.map((d) => ({
          label: `${d.display_name || d.model_name || 'Unnamed draft'} · draft`,
          value: versionRef(d.slug ?? m.slug, 'draft'),
          search: `${group} ${d.display_name ?? ''} ${d.model_name ?? ''} draft`,
        }));
        const runs = m.runs
          .filter((r) => !r.draft)
          .filter((r) => !opts.clonableOnly || canStartDraftFrom(r))
          .map((r) => ({
            label: [
              r.display_name || r.model_name,
              r.started_at ? RUN_STAMP.format(new Date(r.started_at)) : null,
            ]
              .filter(Boolean)
              .join(' · '),
            value: runRef(r.run_id),
            search: `${group} ${r.display_name ?? ''} ${r.model_name} ${r.as_of_dtme}`,
          }));
        return { label: group, items: [...draftItems, ...runs] };
      })
      .filter((g) => g.items.length > 0)
  );
}

export function isRunActive(run: RunRow | null | undefined): boolean {
  return run?.status === 'queued' || run?.status === 'running';
}

/** Percent done of a running model run, or null while the API has not said. */
export function runProgressPercent(run: RunRow | null | undefined): number | null {
  const p = run?.progress;
  if (!p || p.total <= 0) return null;
  return Math.round((100 * Math.min(p.done, p.total)) / p.total);
}

/**
 * "params (2 of 5)", or the bare step while the API has no total yet: a run
 * reports `total: 0` before it has counted its steps, and "2 of 0" reads as broken.
 */
export function runProgressLabel(run: RunRow | null | undefined): string {
  const p = run?.progress;
  if (!p) return '';
  return p.total > 0 ? `${p.step} (${Math.min(p.done, p.total)} of ${p.total})` : p.step;
}

/** "9 min" / "45 s" — run length as the Models table shows it. */
export function formatSeconds(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '';
  if (seconds < 60) return `${Math.round(seconds)} s`;
  return `${Math.round(seconds / 60)} min`;
}

/** What a card's origin is called on screen (the API says house / yours). */
export const BADGE_LABEL: Record<CardBadge, string> = {
  house: 'Standard',
  yours: 'Custom',
};

export const STATE_LABEL: Record<CardState, string> = {
  active: '',
  off: 'Off',
  needs_decision: 'needs a decision',
};

export const STATE_SEVERITY: Record<CardState, TagSeverity> = {
  active: 'secondary',
  off: 'secondary',
  needs_decision: 'warn',
};

export const BANNER_SEVERITY: Record<BannerKind, 'info' | 'warn' | 'success' | 'error'> = {
  info: 'info',
  warn: 'warn',
  placed: 'success',
  refused: 'error',
};

/**
 * Colour class of one card in a diff: added green, removed red with a strike,
 * changed amber, moved amber too (its row says where it moved from and to).
 */
export const DIFF_CHANGE_CLASS: Record<CardChange, string> = {
  added: 'bs-diff--added',
  removed: 'bs-diff--removed',
  changed: 'bs-diff--changed',
  moved: 'bs-diff--moved',
};

export const DIFF_CHANGE_LABEL: Record<CardChange, string> = {
  added: 'added',
  removed: 'removed',
  changed: 'changed',
  moved: 'moved',
};

export function diffChangeClass(change: CardChange): string {
  return DIFF_CHANGE_CLASS[change];
}

/** "position 4 → 6"; a side the card is absent from reads as a dash. */
export function movedLabel(position: { a: number | null; b: number | null } | null): string {
  if (!position) return '';
  const side = (n: number | null) => (n === null ? '—' : String(n));
  return `position ${side(position.a)} → ${side(position.b)}`;
}

/** Sign-prefixed percent, e.g. +0.34% / −0.32%. */
export function signedPercent(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${Math.abs(value).toFixed(digits)}%`;
}

/** Parses a diff ref into what the pickers hold. */
type ParsedRef =
  | { kind: 'version'; slug: string; version: number }
  | { kind: 'draft'; slug: string }
  | { kind: 'run'; runId: string };

export function parseRef(ref: string | null | undefined): ParsedRef | null {
  if (!ref) return null;
  if (ref.startsWith('run:')) {
    const runId = ref.slice(4);
    return runId ? { kind: 'run', runId } : null;
  }
  const at = ref.lastIndexOf('@');
  if (at <= 0) return null;
  // the API's resolve_ref reads `slug@draft` as the open draft, so the page does too
  if (ref.slice(at + 1) === 'draft') return { kind: 'draft', slug: ref.slice(0, at) };
  const version = Number(ref.slice(at + 1));
  return Number.isInteger(version) && version > 0
    ? { kind: 'version', slug: ref.slice(0, at), version }
    : null;
}

/**
 * A covers or subset condition as a line a person reads: "region is United States; line type is
 * product or trade_in". A subset keeps its columns under update_subset_cols.
 */
export function conditionText(raw: unknown): string {
  const obj =
    raw && typeof raw === 'object' && 'update_subset_cols' in raw
      ? (raw as { update_subset_cols: unknown }).update_subset_cols
      : raw;
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return 'All sales';
  const parts = Object.entries(obj as Record<string, unknown>)
    .map(([col, vals]) => {
      const list = (Array.isArray(vals) ? vals : [vals]).map(String).filter(Boolean);
      return list.length ? `${col} is ${list.join(' or ')}` : '';
    })
    .filter(Boolean);
  return parts.length ? parts.join('; ') : 'All sales';
}

/**
 * Every group of the tenant, once each and in name order: the named groups the API lists
 * (a group made with "New group" holds no model yet) and each model's own group.
 */
export function groupsOf(
  models: Pick<ModelsResponse, 'models' | 'groups'> | null | undefined
): string[] {
  const named = models?.groups ?? [];
  // the card of the models in no group is not a group
  const own = (models?.models ?? []).filter((m) => m.group !== null).map((m) => m.group ?? m.title);
  return [...new Set([...named, ...own])].sort((a, b) => a.localeCompare(b));
}

/** What the card of the models in no group is called on screen. */
const NO_GROUP_LABEL = 'Not in a group';
/** The state key of that card; never a name a person can type, so no real group collides with it. */
export const NO_GROUP_KEY = '\u0000none';

/**
 * A card's name on screen: its group; the lineage title on an older API build that named
 * no group; "Not in a group" for the one card of the models in none (Michael, 10/28).
 */
export function cardLabel(card: Pick<ModelCard, 'group' | 'title'>): string {
  return card.group === null ? NO_GROUP_LABEL : (card.group ?? card.title);
}
